import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { AeonyxWordmark } from "@/components/aeonyx/Logo";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/components/ui/tabs";
import { toast } from "sonner";
import {
  ProductsAPI,
  BlogsAPI,
  FaqsAPI,
  OrdersAPI,
  PreOrdersAPI,
} from "@/lib/supabaseServices";
import { Trash, PencilSimple, Eye, EyeSlash } from "@phosphor-icons/react";

const ADMIN_KEY_STORE = "aeonyx-admin-key";

/**
 * Minimal admin dashboard. Access is gated by a shared admin token stored in
 * localStorage. For production, replace this gate with Supabase Auth + the
 * `is_admin()` RLS helper you already have in the DB.
 */
export default function Admin() {
  const [authed, setAuthed] = useState(
    typeof window !== "undefined" && !!localStorage.getItem(ADMIN_KEY_STORE),
  );
  const [key, setKey] = useState("");

  const login = (e) => {
    e.preventDefault();
    if (!key) return;
    localStorage.setItem(ADMIN_KEY_STORE, key);
    setAuthed(true);
    toast.success("Signed in to admin");
  };
  const logout = () => {
    localStorage.removeItem(ADMIN_KEY_STORE);
    setAuthed(false);
  };

  if (!authed) {
    return (
      <div className="min-h-screen bg-cool-white flex items-center justify-center p-6">
        <form onSubmit={login} className="w-full max-w-sm rounded-3xl bg-white border border-[#8A97A8]/25 p-8 space-y-4">
          <Link to="/"><AeonyxWordmark className="h-10 w-auto" /></Link>
          <h1 className="font-display text-2xl font-bold text-navy-deep">Admin sign-in</h1>
          <Label htmlFor="k" className="text-xs text-navy-deep">Admin key</Label>
          <Input id="k" type="password" value={key} onChange={(e)=>setKey(e.target.value)} className="h-11 rounded-xl" required />
          <Button type="submit" className="w-full h-11 rounded-full bg-cta-gradient text-white font-semibold">Enter</Button>
        </form>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-cool-white">
      <header className="max-w-[1400px] mx-auto px-6 py-5 flex items-center justify-between">
        <Link to="/"><AeonyxWordmark className="h-10 w-auto" /></Link>
        <Button variant="outline" onClick={logout} className="rounded-full h-9 text-sm">Sign out</Button>
      </header>
      <main className="max-w-[1400px] mx-auto px-6 pb-24">
        <h1 className="font-display text-3xl md:text-4xl font-bold text-navy-deep">Admin</h1>
        <Tabs defaultValue="products" className="mt-6">
          <TabsList className="rounded-full bg-white border border-[#8A97A8]/25 p-1">
            <TabsTrigger value="products" className="rounded-full">Products</TabsTrigger>
            <TabsTrigger value="blogs" className="rounded-full">Blogs</TabsTrigger>
            <TabsTrigger value="faqs" className="rounded-full">FAQs</TabsTrigger>
            <TabsTrigger value="orders" className="rounded-full">Orders</TabsTrigger>
            <TabsTrigger value="preorders" className="rounded-full">Pre-Orders</TabsTrigger>
          </TabsList>
          <TabsContent value="products"><CrudPanel api={ProductsAPI}  fields={PRODUCT_FIELDS}  labelKey="name"  supportsPublish /></TabsContent>
          <TabsContent value="blogs"><CrudPanel   api={BlogsAPI}     fields={BLOG_FIELDS}     labelKey="title" supportsPublish /></TabsContent>
          <TabsContent value="faqs"><CrudPanel    api={FaqsAPI}      fields={FAQ_FIELDS}      labelKey="question" /></TabsContent>
          <TabsContent value="orders"><StatusPanel api={OrdersAPI}    labelKey="order_number" statusField="order_status" options={["processing","shipped","delivered","cancelled","returned"]} /></TabsContent>
          <TabsContent value="preorders"><StatusPanel api={PreOrdersAPI} labelKey="email"     statusField="status"       options={["new","contacted","converted","cancelled"]} /></TabsContent>
        </Tabs>
      </main>
    </div>
  );
}

const PRODUCT_FIELDS = [
  { name: "name",   label: "Name",        type: "text",   required: true },
  { name: "slug",   label: "Slug",        type: "text" },
  { name: "price",  label: "Price",       type: "number", required: true },
  { name: "stock",  label: "Stock",       type: "number" },
  { name: "featured_image", label: "Featured image URL", type: "text" },
  { name: "short_description", label: "Short description", type: "textarea" },
];
const BLOG_FIELDS = [
  { name: "title",   label: "Title", type: "text", required: true },
  { name: "slug",    label: "Slug",  type: "text" },
  { name: "excerpt", label: "Excerpt", type: "textarea" },
  { name: "content", label: "Content", type: "textarea" },
  { name: "featured_image", label: "Featured image URL", type: "text" },
  { name: "author",  label: "Author", type: "text" },
];
const FAQ_FIELDS = [
  { name: "question", label: "Question", type: "text", required: true },
  { name: "answer",   label: "Answer",   type: "textarea", required: true },
  { name: "sort_order", label: "Sort order", type: "number" },
];

function CrudPanel({ api, fields, labelKey, supportsPublish }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({});

  const load = async () => {
    setLoading(true);
    const res = api.list ? await api.list({ limit: 100 }) : { data: [], error: null };
    if (res.error) toast.error(res.error.message);
    setItems(res.data || []);
    setLoading(false);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const startNew = () => { setEditing("new"); setForm({}); };
  const startEdit = (row) => { setEditing(row.id); setForm(row); };

  const save = async () => {
    const clean = Object.fromEntries(
      Object.entries(form).filter(([k]) => fields.some((f) => f.name === k)),
    );
    const res = editing === "new" ? await api.create(clean) : await api.update(editing, clean);
    if (res.error) return toast.error(res.error.message);
    toast.success("Saved");
    setEditing(null); setForm({}); load();
  };
  const remove = async (id) => {
    if (!window.confirm("Delete this item?")) return;
    const res = await api.remove(id);
    if (res.error) return toast.error(res.error.message);
    toast.success("Deleted"); load();
  };
  const togglePublish = async (row) => {
    if (!api.publish) return;
    const published = row.status === "published";
    const res = await api.publish(row.id, !published);
    if (res.error) return toast.error(res.error.message);
    load();
  };

  return (
    <div className="mt-6 space-y-4">
      <div className="flex justify-between items-center">
        <div className="text-sm text-slate-600">{loading ? "Loading…" : `${items.length} items`}</div>
        <Button onClick={startNew} className="rounded-full h-9 bg-navy-deep text-white text-sm">+ New</Button>
      </div>
      <div className="rounded-2xl bg-white border border-[#8A97A8]/25 divide-y">
        {items.map((it) => (
          <div key={it.id} className="flex items-center justify-between p-4">
            <div>
              <div className="font-medium text-navy-deep">{it[labelKey] || it.id}</div>
              {it.status && <div className="text-xs text-cool-grey mt-0.5">{it.status}</div>}
            </div>
            <div className="flex items-center gap-2">
              {supportsPublish && (
                <Button variant="ghost" size="icon" onClick={() => togglePublish(it)} title={it.status === "published" ? "Unpublish" : "Publish"}>
                  {it.status === "published" ? <EyeSlash size={16} /> : <Eye size={16} />}
                </Button>
              )}
              <Button variant="ghost" size="icon" onClick={() => startEdit(it)}><PencilSimple size={16} /></Button>
              <Button variant="ghost" size="icon" onClick={() => remove(it.id)}><Trash size={16} className="text-rose-600" /></Button>
            </div>
          </div>
        ))}
        {!loading && !items.length && <div className="p-8 text-sm text-cool-grey text-center">No items yet.</div>}
      </div>

      {editing && (
        <div className="rounded-2xl bg-white border border-[#8A97A8]/25 p-6 space-y-4">
          <div className="font-display font-bold text-navy-deep">{editing === "new" ? "New item" : "Edit item"}</div>
          {fields.map((f) => (
            <div key={f.name}>
              <Label className="text-xs text-navy-deep">{f.label}{f.required && " *"}</Label>
              {f.type === "textarea" ? (
                <Textarea rows={4} className="mt-1.5 rounded-xl" value={form[f.name] || ""} onChange={(e) => setForm({ ...form, [f.name]: e.target.value })} />
              ) : (
                <Input type={f.type} required={f.required} className="mt-1.5 h-11 rounded-xl" value={form[f.name] ?? ""} onChange={(e) => setForm({ ...form, [f.name]: f.type === "number" ? Number(e.target.value) : e.target.value })} />
              )}
            </div>
          ))}
          <div className="flex gap-2">
            <Button onClick={save} className="rounded-full h-10 bg-cta-gradient text-white font-semibold">Save</Button>
            <Button variant="outline" onClick={() => { setEditing(null); setForm({}); }} className="rounded-full h-10">Cancel</Button>
          </div>
        </div>
      )}
    </div>
  );
}

function StatusPanel({ api, labelKey, statusField, options }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const load = async () => {
    setLoading(true);
    const { data, error } = await api.list({ limit: 100 });
    if (error) toast.error(error.message);
    setItems(data || []);
    setLoading(false);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const update = async (id, status) => {
    const patch = statusField === "status" ? { status } : { [statusField]: status };
    const res = api.updateStatus ? await api.updateStatus(id, patch) : await api.update(id, patch);
    if (res.error) return toast.error(res.error.message);
    toast.success("Updated"); load();
  };

  return (
    <div className="mt-6 rounded-2xl bg-white border border-[#8A97A8]/25 divide-y">
      {loading && <div className="p-6 text-sm text-cool-grey">Loading…</div>}
      {items.map((it) => (
        <div key={it.id} className="flex items-center justify-between p-4 gap-4">
          <div>
            <div className="font-medium text-navy-deep">{it[labelKey] || it.id}</div>
            <div className="text-xs text-cool-grey mt-0.5">{new Date(it.created_at).toLocaleString()}</div>
          </div>
          <select
            value={it[statusField] || ""}
            onChange={(e) => update(it.id, e.target.value)}
            className="h-9 rounded-full border border-[#8A97A8]/30 px-4 text-sm bg-white"
          >
            {options.map((o) => <option key={o} value={o}>{o}</option>)}
          </select>
        </div>
      ))}
      {!loading && !items.length && <div className="p-8 text-sm text-cool-grey text-center">No entries.</div>}
    </div>
  );
}
